import React, {lazy} from 'react';
import {useSelector, useStore} from 'react-redux';
import {useMatchQuery} from '~library/hooks/useMatchQuery';
import {useBrowserWidth} from '~/common/hooks/useBrowserWidth';
import {isCard, isHost, isPreview} from '~/ViewInvitation/utils';
import {EditLineIcon} from '~library/svg/EditLineIcon';
import {isFabric} from '~/common/utils/event_utils';
import {tanzanite, smallAndUp} from '~libSass/base/_exports.sass';
import linerShading from '~images/liner_shading.png';
import {useStickerCanvas} from '~/common/canvas/hooks/useStickerCanvas';
import './Premium.sass';

const LottiAnimation = lazy(() =>
  import('./LottiAnimation').then((module) => ({default: module.LottiAnimation}))
);

const Card = lazy(() =>
  import('~/common/canvas/Card/Card').then((module) => ({default: module.Card}))
);

export const MainAssetSlide = ({
  isWebview,
  invitation,
  fabricArt,
  envelope,
  stamp,
  guestName,
  liner,
  isLotti,
  yourInvitationRef,
  yourInvitationSizeRef,
  inviteContainerRef,
  desktopInviteRef,
  envelopeWidthRef,
  fabricCardRef,
  stickerContainerRef,
  origin,
  envelopeStickers,
}) => {
  const {matches: mediumScreens} = useMatchQuery(`(min-width: ${smallAndUp})`);
  const store = useStore();
  const browserWidth = useBrowserWidth();

  const [editArtworkClicked, setEditArtworkClicked] = React.useState(false);
  const {canvasRef} = useStickerCanvas({
    configJson: envelopeStickers,
    onChange: () => null,
    setCanvas: () => null,
  });

  const background = useSelector((state) => state.premiumDesign.background);

  let invitee;
  if (isHost() || isPreview()) {
    // host and preview should never see their name on envelope
    if (isCard()) {
      invitee = "Recipient's Name";
    } else {
      invitee = "Guest's Name";
    }
  } else if (isWebview) {
    invitee = window.guest_name ?? 'Hello there!';
  } else if (guestName === 'AnonymousUser') {
    // for shareable links, display generic messages
    if (isCard()) {
      invitee = 'Hello there!';
    } else {
      invitee = "You're Invited";
    }
  } else if (guestName && guestName !== ' ') {
    // if the user has a guest name, show it on envelope
    invitee = guestName;
  } else {
    // if no guest name is available, show a generic message
    invitee = 'Hello there!';
  }

  const {image: inviteImg, bodymovinAssets, bodymovinAnimation, renderedTextImg} = invitation;
  const renderedTextImgStyle = {};

  const containerHeight = () => {
    if (isWebview) {
      return '100vh';
    }
    if (browserWidth < 500) {
      return '512px';
    }
    let contHeight = parseInt(window.innerHeight, 10) - 180;
    contHeight = contHeight > 1000 ? 1000 : contHeight;
    return `${contHeight}px`;
  };

  const scaleClass = () => {
    if (!isWebview && browserWidth > 500) {
      if (window.innerHeight >= 840 && window.innerHeight <= 900) {
        // 840-900
        return 'scale-90';
      }
      if (window.innerHeight >= 800 && window.innerHeight < 840) {
        // 800-839
        return 'scale-80';
      }
      if (window.innerHeight > 700 && window.innerHeight < 800) {
        // 701-799
        return 'scale-70';
      }
      if (window.innerHeight <= 700) {
        // 700 and down
        return 'scale-60';
      }
    }
    return '';
  };

  const getAsset = () => {
    if (isFabric(origin)) {
      return (
        <div
          className="animation-image invite-envelope__your-invitation fabric"
          ref={fabricCardRef}
        >
          <div className="invite-envelope__your-invitation__card">
            <Card fabricArt={fabricArt} imageRef={yourInvitationRef} />
            <button
              className="edit-artwork-btn hidden"
              id="editArtworkBtn"
              data-qa-id="edit-artwork"
              type="button"
              onClick={() => {
                setEditArtworkClicked(true);
                window.navigateToRoute('customize', store).then(() => setEditArtworkClicked(false));
              }}
            >
              {editArtworkClicked && <span className="evt-spinner inline dark" />}
              <EditLineIcon ratio={0.5} color={tanzanite} />
              {mediumScreens && <>Edit Artwork</>}
            </button>
          </div>
        </div>
      );
    }
    if (isLotti) {
      return (
        <div className="lotti-anim invite-envelope__your-invitation">
          <LottiAnimation
            height="auto"
            renderedTextImgStyle={renderedTextImgStyle}
            {...{bodymovinAssets, bodymovinAnimation, renderedTextImg}}
            innerRef={yourInvitationRef}
          />
        </div>
      );
    }
    return (
      <div className="animation-image invite-envelope__your-invitation fabric" ref={fabricCardRef}>
        <div className="invite-envelope__your-invitation__card">
          <img
            className="legacy_user_card"
            ref={yourInvitationRef}
            src={inviteImg}
            alt="invitation"
          />
        </div>
      </div>
    );
  };

  return (
    <div className="asset">
      <div
        className="desktop-invite-container"
        ref={desktopInviteRef}
        style={{
          height: containerHeight(), // desktop: height, webview: 100vh, and 512 for MW
          backgroundColor: isWebview ? background?.color : 'transparent',
          backgroundImage: isWebview ? `url(${background?.imageMobile})` : undefined,
        }}
      >
        <div
          className={`invite-container step-0 ${isFabric(origin) ? 'fabric' : ''} ${scaleClass()}`}
          ref={inviteContainerRef}
        >
          <div className="your-invitation-size-ref" style={{position: 'absolute', opacity: '0'}}>
            <img ref={yourInvitationSizeRef} src={inviteImg} alt="" />
          </div>

          <div className="invite-animation open-flap">
            <div className="animation-image invite-envelope__open-flap-group">
              <img
                className="invite-envelope__open-flap invite-envelope__open-flap-shading"
                src={linerShading}
                alt=""
              />
              <img className="invite-envelope__open-flap" src={envelope?.openBack} alt="" />
              <img className="invite-envelope__top-liner" src={liner?.image} alt="" />
            </div>
          </div>

          <div className="invite-animation open-bottom">
            <img
              className="animation-image invite-envelope__open-bottom"
              src={envelope?.openBack}
              alt=""
            />
            <img
              className="animation-image invite-envelope__bottom-liner"
              src={liner?.image}
              alt=""
            />
            <img
              className="animation-image invite-envelope__bottom-liner-shading"
              src={linerShading}
              alt=""
            />
          </div>

          <div className="invite-animation your-invitation">{getAsset()}</div>

          <div className="invite-animation open-bottom-front">
            <img
              ref={envelopeWidthRef}
              className="animation-image invite-envelope__open-bottom-front"
              src={envelope?.openBack}
              alt=""
            />
          </div>

          <div className="invite-animation closed-flap">
            <div className="animation-image invite-envelope__closed-flap">
              <img className="invite-envelope__full-width" src={envelope?.closedBack} alt="" />
            </div>
          </div>

          <div className="invite-animation closed-front">
            <div className="animation-image invite-envelope__closed-front">
              <img
                className="invite-envelope__full-width"
                src={envelope?.closedFront}
                alt="envelope"
              />
              <img className="invite-envelope__stamp" src={stamp?.image} alt="envelope stamp" />
              <span
                style={{
                  color: envelope?.name?.color,
                  fontFamily: envelope?.name?.font,
                }}
                className="invite-envelope__guest-name"
              >
                {invitee}
              </span>
              <div
                id="canvas__wrapper__stickers"
                className="invite-envelope__closed-front__wrapper__canvas"
                ref={stickerContainerRef}
              >
                <canvas ref={canvasRef} />
              </div>
            </div>
          </div>
          <div className="invisible-width-box">
            <img src={envelope?.openBack} alt="" />
          </div>
        </div>
      </div>
    </div>
  );
};
